import React, { useEffect, useRef, useState } from "react"
import { MdAttachFile, MdOpenInNew, MdSearch, MdSwapHoriz } from "react-icons/md"
import { MobergTheme } from "../../Components/MobergThemes/MobergColors"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../Components/MobergButton/MobergButton"
import { MobergDropdown, MobergDropdownSize } from "../../Components/MobergDropdown/MobergDropdown"
import { fmtDay } from "../Internal/claraDocumentReview"
import { DOCUMENT_TYPES, reviewDistance, statusLabel, typeLabel } from "./claraDocumentsList"
import {
	DateField, Failure, HiddenInput, LinkButton, Muted, Panel, PanelBody, PanelFooter, PlainType, PropGrid, Rank, Result, ResultCitation,
	ResultHead, ResultText, Results, ResultsMeta, ReviewCard, Score, Scores, SearchInput, SearchRow, StatusTag, TypePill, TypeWrap,
} from "./claraDocumentsStyles"

const CLARA_API = window.env?.REACT_APP_CLARA_API_URL

const STATUS_LABEL = { pending: "Queued", indexing: "Being read", ready: "Ready", failed: "Could not read" }
const TYPE_OPTIONS = DOCUMENT_TYPES.map(t => ({ value: t, label: t }))

function fmtTime(iso) {
	if (!iso) return "—"
	return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
}

async function postJson(path, body, getToken) {
	const response = await fetch(`${CLARA_API}${path}`, {
		method: "POST",
		headers: { "Content-Type": "application/json", Authorization: getToken() },
		body: JSON.stringify(body),
	})
	const data = await response.json().catch(() => ({}))
	if (!response.ok) throw new Error(data.error || `status ${response.status}`)
	return data
}

export function TypeBadge({ document, plain = false }) {
	const label = typeLabel(document)
	const Pill = plain ? PlainType : TypePill
	switch (label.tone) {
		case "manual":
			return <TypeWrap><Pill title={label.text}>{label.text}</Pill></TypeWrap>
		case "auto":
			return (
				<TypeWrap title="Detected by Clara from the title and first page">
					<Pill>{label.text}</Pill>
					<Muted>detected</Muted>
				</TypeWrap>
			)
		case "guess":
			return (
				<TypeWrap title={document.type_guess ? `Clara's best guess is ${document.type_guess}` : "Clara could not tell"}>
					<Pill>Needs a type</Pill>
					{document.type_guess && <Muted>guess: {document.type_guess}</Muted>}
				</TypeWrap>
			)
		case "detecting":
			return <Muted style={{ fontStyle: "italic" }}>Detecting type…</Muted>
		default:
			return <Muted>Unclassified</Muted>
	}
}

function reviewCopy(document) {
	const due = fmtDay(document.next_review_date)
	switch (document.review_status) {
		case "overdue":
			return {
				tone: "overdue",
				status: reviewDistance(document.next_review_date),
				explain: "Since this date passed, answers that quote this policy say it may be out of date. Upload the newer version, or change the date if it is wrong.",
			}
		case "acknowledged":
			return {
				tone: "current",
				status: "Confirmed current",
				explain: `The review date ${due} has passed, but someone confirmed this is still the latest version. Clara will not warn about it until ${fmtDay(document.review_acknowledged_until)}.`,
			}
		case "current":
			return {
				tone: "current",
				status: reviewDistance(document.next_review_date),
				explain: "Until this date, answers quote this policy without a warning. After it, they say it may be out of date.",
			}
		default:
			return {
				tone: "none",
				status: "Not set",
				explain: "Clara did not find a review date in this document, so it never shows as out of date. Add one if this is a policy with a review cycle.",
			}
	}
}

export function ClaraQuestionTest({ document, workspace, getToken, onError }) {
	const [question, setQuestion] = useState("")
	const [onlyThis, setOnlyThis] = useState(true)
	const [search, setSearch] = useState(null)
	const [busy, setBusy] = useState(false)

	useEffect(() => {
		setSearch(null)
		setQuestion("")
	}, [document.id])

	async function handleSearch(event) {
		event?.preventDefault()
		if (!question.trim()) return
		setBusy(true)
		try {
			setSearch(await postJson("/documents/search", { workspace_name: workspace, question, ...(onlyThis ? { document_id: document.id } : {}) }, getToken))
		} catch (e) {
			onError(`Could not search: ${e.message}`)
		} finally {
			setBusy(false)
		}
	}

	return (
		<form onSubmit={handleSearch} style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 760 }}>
			<Muted style={{ fontSize: 14 }}>Ask what a nurse would ask and see which passages Clara would read, best first. It does not write an answer.</Muted>
			<SearchRow>
				<SearchInput value={question} onChange={e => setQuestion(e.target.value)} placeholder="Ask something a nurse would ask" aria-label="Test a question" />
				<MobergButton theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE} disabled={busy || !question.trim()}>
					<MdSearch size={16} /> {busy ? "Searching…" : "Test"}
				</MobergButton>
			</SearchRow>
			<Muted as="label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<input type="checkbox" checked={onlyThis} onChange={e => setOnlyThis(e.target.checked)} /> Only this document
			</Muted>
			{search && (
				<Results>
					<ResultsMeta>
						{search.results.length === 0 ? "Nothing matched." : `${search.results.length} passage${search.results.length === 1 ? "" : "s"} · ${search.latency_ms} ms`}
					</ResultsMeta>
					{search.results.map((hit, index) => (
						<Result key={hit.chunk_id}>
							<ResultHead>
								<Rank>{index + 1}</Rank>
								<ResultCitation>{hit.citation}</ResultCitation>
								<Scores>
									{hit.rerank_score != null && <Score $strong>relevance {hit.rerank_score}/10</Score>}
									<Score>similarity {hit.vector_score}</Score>
									<Score>keyword {hit.keyword_score}</Score>
								</Scores>
							</ResultHead>
							<ResultText>{hit.text}</ResultText>
						</Result>
					))}
				</Results>
			)}
		</form>
	)
}

export function ClaraDocumentPanel({ document, canManage, originalsStored, workspace, getToken, onChanged, onDeleted, onError }) {
	const [type, setType] = useState("")
	const [dates, setDates] = useState({ effective: "", due: "" })
	const [editingDates, setEditingDates] = useState(false)
	const [busy, setBusy] = useState(null)
	const replaceInput = useRef(null)
	const attachInput = useRef(null)

	useEffect(() => {
		setType(DOCUMENT_TYPES.includes(document.doc_type) ? document.doc_type : document.type_guess ?? "")
		setDates({ effective: document.effective_date || "", due: document.next_review_date || "" })
	}, [document.id, document.doc_type, document.type_guess, document.effective_date, document.next_review_date])

	useEffect(() => {
		setEditingDates(false)
	}, [document.id])

	async function post(path, body, label) {
		setBusy(label)
		try {
			return await postJson(path, { workspace_name: workspace, ...body }, getToken)
		} catch (e) {
			onError(`Could not ${label}: ${e.message}`)
			return null
		} finally {
			setBusy(null)
		}
	}

	async function saveType() {
		if (await post("/documents/types", { assignments: [{ document_id: document.id, doc_type: type }] }, "save the type")) onChanged()
	}

	async function saveDates() {
		if (await post(`/documents/${document.id}/review`, { effective_date: dates.effective || null, next_review_date: dates.due || null }, "update review dates")) {
			setEditingDates(false)
			onChanged()
		}
	}

	function cancelDates() {
		setDates({ effective: document.effective_date || "", due: document.next_review_date || "" })
		setEditingDates(false)
	}

	async function handleDelete() {
		if (!window.confirm(`Delete "${document.title}"? Clara will no longer be able to answer from it.`)) return
		if (await post(`/documents/${document.id}/delete`, {}, "delete")) onDeleted()
	}

	async function upload(path, field, file, label) {
		setBusy(label)
		try {
			const body = new FormData()
			body.append(field, file)
			const response = await fetch(`${CLARA_API}${path}?workspace_name=${encodeURIComponent(workspace)}`, {
				method: "POST",
				headers: { Authorization: getToken() },
				body,
			})
			const data = await response.json().catch(() => ({}))
			if (!response.ok) throw new Error(data.error || `status ${response.status}`)
			return data
		} finally {
			setBusy(null)
		}
	}

	async function handleReplace(event) {
		const file = event.target.files?.[0]
		event.target.value = ""
		if (!file) return
		try {
			await upload(`/documents/${document.id}/replace`, "file", file, "replace the file")
			onChanged()
		} catch (e) {
			onError(`Could not replace the file: ${e.message}`)
		}
	}

	async function handleAttach(event) {
		const file = event.target.files?.[0]
		event.target.value = ""
		if (!file) return
		try {
			const data = await upload("/documents/originals", "files", file, "attach the PDF")
			const match = data.matched?.[0]
			if (data.failed?.length) throw new Error(`${file.name} ${data.failed[0].error}`)
			if (!match) throw new Error(`${file.name} is not the file Clara read for this document. If it is a newer version, use Upload newer version.`)
			if (match.document_id !== document.id) {
				onError(match.already_stored
					? `${file.name} is the file of "${match.title}", which already has its PDF. Nothing changed here.`
					: `${file.name} is the file of "${match.title}", so it was attached there instead.`)
			}
			onChanged()
		} catch (e) {
			onError(`Could not attach the PDF: ${e.message}`)
		}
	}

	const label = typeLabel(document)
	const status = statusLabel(document)
	const typeChanged = type && type !== document.doc_type
	const review = reviewCopy(document)
	const overdue = document.review_status === "overdue"

	return (
		<Panel aria-label="Document details">
			<PanelBody>
				{canManage && (
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
						{originalsStored && !document.has_original && (
							<MobergButton
								onClick={() => attachInput.current?.click()}
								variant={MobergButtonVariant.OUTLINE}
								theme={MobergTheme.BLUE}
								shape={MobergButtonShape.RECTANGLE}
								disabled={Boolean(busy)}
								tooltip="Adds the PDF Clara read for this document, so it can be opened. The text, type and dates stay as they are."
							>
								<MdAttachFile size={16} /> {busy === "attach the PDF" ? "Attaching…" : "Attach PDF"}
							</MobergButton>
						)}
						<MobergButton
							onClick={() => replaceInput.current?.click()}
							variant={MobergButtonVariant.OUTLINE}
							theme={MobergTheme.BLUE}
							shape={MobergButtonShape.RECTANGLE}
							disabled={Boolean(busy)}
							tooltip="Upload a new version. Clara reads it in place of this one and keeps the type. The old PDF is deleted once the new one is ready."
						>
							<MdSwapHoriz size={16} /> {busy === "replace the file" ? "Uploading…" : "Upload newer version"}
						</MobergButton>
					</div>
				)}

				<PropGrid>
					<dt>Type</dt>
					<dd>
						{canManage ? (
							<>
								<MobergDropdown options={TYPE_OPTIONS} selectedValue={type} onChange={setType} size={MobergDropdownSize.SMALL} width={160} ariaLabel="Document type" />
								{(typeChanged || document.needs_type) && (
									<MobergButton onClick={saveType} variant={MobergButtonVariant.FILLED} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE} disabled={!type || Boolean(busy)}>
										{typeChanged ? "Save" : "Confirm"}
									</MobergButton>
								)}
							</>
						) : (
							<TypeBadge document={document} plain />
						)}
						{label.tone === "auto" && <Muted>read by Clara</Muted>}
						{document.needs_type && <Muted>{document.type_guess ? `Clara guessed ${document.type_guess}` : "Clara could not tell"}</Muted>}
					</dd>
					{status && (
						<>
							<dt>Status</dt>
							<dd>
								<StatusTag $tone={status.tone === "failed" ? "danger" : "info"}>{STATUS_LABEL[document.status] ?? status.text}</StatusTag>
								{document.status === "failed" && document.error && <Failure>{document.error}</Failure>}
							</dd>
						</>
					)}
					<dt>Review by</dt>
					<dd style={overdue ? { color: "var(--color-danger-text-hover)", fontWeight: 600 } : undefined}>{fmtDay(document.next_review_date) ?? <Muted>No date</Muted>}</dd>
					<dt>Effective</dt>
					<dd>{fmtDay(document.effective_date) ?? <Muted>Not found in the PDF</Muted>}</dd>
					<dt>PDF</dt>
					<dd style={{ minWidth: 0 }}>
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={document.filename}>
							{document.has_original || !originalsStored ? document.filename : "No PDF stored, only the text"}
						</span>
					</dd>
					<dt>Number</dt>
					<dd>{document.doc_number || <Muted>None</Muted>}</dd>
					<dt>Added</dt>
					<dd>{fmtTime(document.created_at)}{document.uploaded_by_email && <Muted>by {document.uploaded_by_email}</Muted>}</dd>
					{document.source_url && (
						<>
							<dt>Source</dt>
							<dd><a href={document.source_url} target="_blank" rel="noreferrer">Compliance link <MdOpenInNew size={12} /></a></dd>
						</>
					)}
				</PropGrid>

				<ReviewCard $tone={review.tone} aria-labelledby="clara-review-card">
					<div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
						<h3 id="clara-review-card">Review date</h3>
						<span className="status">{review.status}</span>
					</div>
					{document.next_review_date && <div className="date">{fmtDay(document.next_review_date)}</div>}
					<p>{review.explain}</p>
					{canManage && editingDates && (
						<div className="actions">
							<DateField>
								<span>Review by</span>
								<input type="date" value={dates.due} onChange={e => { const value = e.target.value; setDates(d => ({ ...d, due: value })) }} />
							</DateField>
							<DateField>
								<span>Effective</span>
								<input type="date" value={dates.effective} onChange={e => { const value = e.target.value; setDates(d => ({ ...d, effective: value })) }} />
							</DateField>
							<div style={{ display: "flex", gap: 8 }}>
								<MobergButton onClick={saveDates} variant={MobergButtonVariant.FILLED} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE} disabled={Boolean(busy)}>
									Save dates
								</MobergButton>
								<MobergButton onClick={cancelDates} variant={MobergButtonVariant.OUTLINE} theme={MobergTheme.BLUE} shape={MobergButtonShape.RECTANGLE}>
									Cancel
								</MobergButton>
							</div>
						</div>
					)}
					{canManage && !editingDates && (
						<LinkButton type="button" onClick={() => setEditingDates(true)} style={{ alignSelf: "flex-start", fontSize: 13 }}>
							{document.next_review_date ? "Change the date" : "Add a date"}
						</LinkButton>
					)}
				</ReviewCard>
			</PanelBody>

			{canManage && (
				<PanelFooter>
					<LinkButton type="button" onClick={handleDelete} disabled={Boolean(busy)} style={{ fontSize: 13, color: "var(--color-danger-text-hover)" }}>
						Delete document
					</LinkButton>
				</PanelFooter>
			)}
			<HiddenInput ref={replaceInput} type="file" accept="application/pdf,.pdf" onChange={handleReplace} />
			<HiddenInput ref={attachInput} type="file" accept="application/pdf,.pdf" onChange={handleAttach} />
		</Panel>
	)
}
